/**
 * GraphFindBox — find-in-graph with highlight-on-match (tender sheet #40).
 *
 * Lives in the Canvas Tools bar (bottom centre of the Workbench canvas).
 * Matching is the pure `findGraphMatches` rule over `useGraphStore.elements`
 * (node id/address, label/entity name, edge tx hash). Highlighting follows
 * R11: non-matches are dimmed to DIMMED through `transientVisibility` under
 * the 'find' tag, which hands the previous `_visibility` back on clear. No
 * CSS class carries opacity, and nothing is written to the store, so a find
 * never leaks into a snapshot or an undo step.
 *
 * Keys: Enter = next match, Shift+Enter = previous, Esc = clear.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import type cytoscape from 'cytoscape';
import { VISIBILITY } from '../../constants/visibility';
import { clearTransient, dimTransient } from '../../utils/transientVisibility';
import { findGraphMatches, matchElementData, nextFindIndex } from '../../utils/graphFind';
import type { FindableElement, GraphFindMatch } from '../../utils/graphFind';

interface GraphFindBoxProps {
    elements: readonly FindableElement[];
    getCy: () => cytoscape.Core | null | undefined;
    /** Select a node match (opens it in the Node Inspector). */
    onSelectNode: (nodeId: string, data: Record<string, any>) => void;
    /** Select an edge match (opens its pair in the Transfers tab). */
    onSelectEdge: (data: Record<string, any>) => void;
}

export const GraphFindBox: React.FC<GraphFindBoxProps> = ({ elements, getCy, onSelectNode, onSelectEdge }) => {
    const [query, setQuery] = useState('');
    const [index, setIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);

    const matches: GraphFindMatch[] = useMemo(() => findGraphMatches(elements, query), [elements, query]);

    const liveCy = useCallback(() => {
        const cy = getCy();
        return cy && !cy.destroyed() ? cy : null;
    }, [getCy]);

    /** Matches that are on the canvas now (stashed ones are counted, not shown). */
    const onCanvas = useCallback((cy: cytoscape.Core) => {
        const ids = new Set(matches.map((m) => m.id));
        // Consolidated canvas edges may carry ids the store does not; match their data too.
        return cy.elements().filter((ele) => ids.has(ele.id()) || (query.trim() !== '' && matchElementData(ele.data(), query) !== null));
    }, [matches, query]);

    const select = useCallback((match: GraphFindMatch, center: boolean) => {
        const cy = liveCy();
        const el = elements.find((e) => e?.data?.id === match.id);
        const data = (el?.data || { id: match.id }) as Record<string, any>;
        if (cy) {
            const ele = cy.getElementById(match.id);
            if (ele.length) {
                cy.elements().unselect();
                ele.select();
                if (center) cy.animate({ center: { eles: ele }, duration: 300 } as any);
            }
        }
        if (match.kind === 'node') onSelectNode(match.id, data);
        else onSelectEdge(data);
    }, [elements, liveCy, onSelectNode, onSelectEdge]);

    // Apply / restore the dim whenever the query or the canvas changes.
    useEffect(() => {
        const cy = liveCy();
        if (!cy) return;
        cy.batch(() => {
            clearTransient(cy.elements(), 'find');
            if (!query.trim()) return;
            const hits = onCanvas(cy);
            const keep = hits.union(hits.edges().connectedNodes());
            dimTransient(cy.elements().not(keep), VISIBILITY.DIMMED, 'find');
        });
    }, [query, elements, liveCy, onCanvas]);

    // New query: select the first match and fit the viewport on all matches.
    useEffect(() => {
        if (!query.trim() || matches.length === 0) {
            setIndex(-1);
            return;
        }
        setIndex(0);
        select(matches[0], false);
        const cy = liveCy();
        if (cy) {
            const hits = onCanvas(cy);
            if (hits.length > 0) {
                const frame = hits.union(hits.edges().connectedNodes());
                cy.animate({ fit: { eles: frame, padding: 60 }, duration: 350 } as any);
            }
        }
        // Only on a new query; cycling is driven by Enter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    // Restore everything on unmount.
    useEffect(() => () => {
        const cy = liveCy();
        if (cy) clearTransient(cy.elements(), 'find');
    }, [liveCy]);

    const clear = () => {
        setQuery('');
        setIndex(-1);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            clear();
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            if (matches.length === 0) return;
            const next = nextFindIndex(index, matches.length, e.shiftKey);
            setIndex(next);
            select(matches[next], true);
        }
        // Keep canvas keyboard shortcuts from firing while typing.
        e.stopPropagation();
    };

    const total = matches.length;
    const hasQuery = query.trim() !== '';
    const status = !hasQuery
        ? null
        : total === 0
            ? 'No matches'
            : `${total} ${total === 1 ? 'match' : 'matches'}${total > 1 && index >= 0 ? ` · ${index + 1}/${total}` : ''}`;

    return (
        <div
            data-tour="graph-find"
            data-testid="wb-graph-find"
            role="search"
            className="flex items-center gap-1 rounded-md border border-border/60 bg-canvas-surface/80 px-1.5 py-1"
        >
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden />
            <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Find in graph"
                aria-label="Find in graph: address, label, entity or tx hash"
                title="Find in graph — address, label, entity name or tx hash. Enter: next match, Shift+Enter: previous, Esc: clear"
                data-testid="wb-graph-find-input"
                className="w-36 bg-transparent text-[11px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
            {status && (
                <span
                    data-testid="wb-graph-find-count"
                    aria-live="polite"
                    className={`text-[10px] whitespace-nowrap ${total === 0 ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                    {status}
                </span>
            )}
            {hasQuery && (
                <button
                    type="button"
                    onClick={clear}
                    aria-label="Clear find"
                    data-testid="wb-graph-find-clear"
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                >
                    <X className="w-3 h-3" />
                </button>
            )}
        </div>
    );
};
